// callback.jsx
import React, { useState } from "react";
import "../css/callback.css";

export default function CallbackForm() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    locality: "",
    acType: "",
    issue: "",
  });
  const [status, setStatus] = useState(null);
  const [touched, setTouched] = useState({}); // track touched fields
  const [fieldErrors, setFieldErrors] = useState({});

  const SURAT_AREAS = [
    "Adajan",
    "Adajan Gam",
    "Afwa",
    "Ahura Nagar",
    "Althan",
    "Aman Nagar",
    "Amroli",
    "Anand Mahal Road",
    "Anand Nagar",
    "Anjada Nagar",
    "Athwa",
    "Athwa Gate",
    "Athwalines",
    "Bamroli",
    "Bardoli",
    "Begumpura",
    "Bhesan",
    "Bhestan",
    "Bhestan Gam",
    "Bhanodra",
    "Bharthana",
    "Chala",
    "Dindoli",
    "Dumas",
    "GIDC",
    "Ghogha",
    "Godadara",
    "Hazira",
    "Jahangirpura",
    "Kagdi",
    "Katargam",
    "Khatodara",
    "Khajod",
    "Kumbhariya",
    "Majura",
    "Mota Varachha",
    "Nanpura",
    "Nana Varachha",
    "Piplod",
    "Rander",
    "Ring Road",
    "Sarthana",
    "Saroli",
    "Surat Railway Station",
    "Udhna",
    "Udhna Darwaja",
    "Utran",
    "Umarwada",
    "Varachha",
    "Vesu",
    "VIP Road",
    "Yogichowk",
    "City Light",
    "Puna",
    "Olpad",
    "Khajod Industrial Area",
    "Palanpur",
    "Dharampur",
  ];

  const AC_TYPES = ["Split AC", "Window AC", "Cassette AC"];
  const ISSUES = [
    "Installation",
    "Gas Filling",
    "Pipe Leakage",
    "Ice Formation",
    "Servicing - Normal",
    "Servicing - Chemical Cleaning",
    "Stabilizer Fitting",
  ];

  function handleChange(e) {
    const { name, value } = e.target;
    // For phone, strip non-digits
    if (name === "phone") {
      const cleaned = value.replace(/\D/g, "");
      setForm((p) => ({ ...p, phone: cleaned }));
    } else {
      setForm((p) => ({ ...p, [name]: value }));
    }
    // clear field error while typing
    setFieldErrors((prev) => ({ ...prev, [name]: null }));
  }

  function handleBlur(e) {
    const { name } = e.target;
    setTouched((t) => ({ ...t, [name]: true }));
    const err = validateField(name, form[name]);
    setFieldErrors((prev) => ({ ...prev, [name]: err }));
  }

  function validateField(name, value) {
    if (name === "name") {
      if (!value || !value.trim()) return "Please enter your name.";
      if (value.trim().length < 2) return "Name must be at least 2 characters.";
      return null;
    }
    if (name === "email") {
      // Email is OPTIONAL: only validate when provided
      if (!value || !value.trim()) return null;
      // simple email regex
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value)) return "Please enter a valid email address.";
      return null;
    }
    if (name === "phone") {
      const digits = (value || "").replace(/\D/g, "");
      // Accept 10 digits for Indian mobile numbers
      if (!digits) return "Please enter your phone number.";
      if (digits.length !== 10) return "Please enter a 10-digit mobile number (without leading 0).";
      if (!/^[6-9]\d{9}$/.test(digits)) return "Enter a valid Indian mobile number (starting with 6-9).";
      return null;
    }
    if (name === "locality") {
      if (!value || !value.trim()) return "Please select your locality.";
      return null;
    }
    if (name === "acType") {
      if (!value) return "Please select your AC type.";
      return null;
    }
    if (name === "issue") {
      if (!value) return "Please select the issue you are facing.";
      return null;
    }
    return null;
  }

  function validate() {
    const errors = {};
    errors.name = validateField("name", form.name);
    // email optional -> validate only if provided (validateField handles this)
    errors.email = validateField("email", form.email);
    errors.phone = validateField("phone", form.phone);
    errors.locality = validateField("locality", form.locality);
    errors.acType = validateField("acType", form.acType);
    errors.issue = validateField("issue", form.issue);

    // keep only fields with errors
    const hasError = Object.values(errors).some(Boolean);
    setFieldErrors(errors);
    return hasError ? errors : null;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setTouched({
      name: true,
      email: true,
      phone: true,
      locality: true,
      acType: true,
      issue: true,
    });

    const errs = validate();
    if (errs) {
      setStatus({ type: "error", message: "Please fix the highlighted fields." });
      return;
    }

    setStatus({ type: "loading", message: "Preparing message..." });

    // Owner's number (international format without +) — India +91
    const ownerNumberInternational = "91" + "7237898275"; // +91 72378 98275

    // Prepare phone with +91 prefixed
    const mobileDigits = form.phone.replace(/\D/g, "");
    const phoneToSend = mobileDigits.length === 10 ? `+91${mobileDigits}` : `+91${mobileDigits}`;

    // If email empty, mark as Not provided
    const emailToSend = form.email && form.email.trim() ? form.email.trim() : "Not provided";

    // Build clear well-structured message
    const messageLines = [
      "New Call Back Request:",
      `Name: ${form.name}`,
      `Email: ${emailToSend}`,
      `Phone: ${phoneToSend}`,
      `Locality: ${form.locality}`,
      `AC Type: ${form.acType}`,
      `Issue: ${form.issue}`,
      "-- End --",
    ];

    const message = encodeURIComponent(messageLines.join("\n"));
    const waLink = `https://wa.me/${ownerNumberInternational}?text=${message}`;

    // Open WhatsApp Web/App
    try {
      window.open(waLink, "_blank");
      setStatus({ type: "success", message: "Submitted — opening WhatsApp to send details to the owner." });
      // Reset form after a short delay for good UX
      setTimeout(() => {
        setForm({ name: "", email: "", phone: "", locality: "", acType: "", issue: "" });
        setTouched({});
        setFieldErrors({});
      }, 900);
    } catch (e) {
      // Fallback: copy message to clipboard
      try {
        await navigator.clipboard.writeText(messageLines.join("\n"));
        setStatus({ type: "success", message: "Message copied to clipboard — please share with 72378 98275." });
      } catch (err) {
        setStatus({ type: "error", message: "Unable to open WhatsApp or copy message. Please contact the owner at 72378 98275." });
      }
    }
  }

  return (
    <div className="callback-wrapper">
      <div className="callback-card" role="region" aria-label="Request A Call Back form">
        <h2 className="callback-title">Request A Call Back</h2>
        <form className="callback-form" onSubmit={handleSubmit} noValidate>
          <div className="row">
            <label className={`field ${fieldErrors.name && touched.name ? "error" : ""}`}>
              <span className="label">Name</span>
              <input
                name="name"
                value={form.name}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Your name"
                type="text"
                autoComplete="name"
                aria-invalid={!!(fieldErrors.name && touched.name)}
                aria-describedby={fieldErrors.name && touched.name ? "err-name" : undefined}
              />
              {fieldErrors.name && touched.name && (
                <div id="err-name" className="field-error">{fieldErrors.name}</div>
              )}
            </label>

            <label className={`field ${fieldErrors.email && touched.email ? "error" : ""}`}>
              <span className="label">Email <span style={{fontWeight:500, fontSize:12, color:"#94a3b8"}}>(optional)</span></span>
              <input
                name="email"
                value={form.email}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="your@email.com"
                type="email"
                autoComplete="email"
                aria-invalid={!!(fieldErrors.email && touched.email)}
                aria-describedby={fieldErrors.email && touched.email ? "err-email" : undefined}
              />
              {fieldErrors.email && touched.email && (
                <div id="err-email" className="field-error">{fieldErrors.email}</div>
              )}
            </label>
          </div>

          <div className="row">
            <label className={`field phone-field ${fieldErrors.phone && touched.phone ? "error" : ""}`}>
              <span className="label">Phone Number</span>
              <div className="phone-input-wrap">
                <span className="phone-prefix">+91</span>
                <input
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Enter 10-digit mobile number"
                  type="tel"
                  inputMode="numeric"
                  autoComplete="tel"
                  maxLength={10}
                  aria-invalid={!!(fieldErrors.phone && touched.phone)}
                  aria-describedby={fieldErrors.phone && touched.phone ? "err-phone" : undefined}
                />
              </div>
              {fieldErrors.phone && touched.phone && (
                <div id="err-phone" className="field-error">{fieldErrors.phone}</div>
              )}
            </label>

            <label className={`field ${fieldErrors.locality && touched.locality ? "error" : ""}`}>
              <span className="label">Locality of Service</span>

              <select
                className="desktop-only"
                name="locality"
                value={form.locality}
                onChange={handleChange}
                onBlur={handleBlur}
                aria-label="Locality of Service"
                aria-invalid={!!(fieldErrors.locality && touched.locality)}
                aria-describedby={fieldErrors.locality && touched.locality ? "err-locality" : undefined}
              >
                <option value="">Select locality of service</option>
                {SURAT_AREAS.map((a) => (
                  <option key={a} value={a}>
                    {a}
                  </option>
                ))}
              </select>

              <input
                className="mobile-only"
                list="surat-areas"
                name="locality"
                value={form.locality}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Select or type locality"
                aria-label="Locality of Service"
                aria-invalid={!!(fieldErrors.locality && touched.locality)}
                aria-describedby={fieldErrors.locality && touched.locality ? "err-locality" : undefined}
              />
              <datalist id="surat-areas">
                {SURAT_AREAS.map((a) => (
                  <option key={a} value={a} />
                ))}
              </datalist>

              {fieldErrors.locality && touched.locality && (
                <div id="err-locality" className="field-error">{fieldErrors.locality}</div>
              )}
            </label>
          </div>

          <div className="row">
            <label className={`field full ${fieldErrors.acType && touched.acType ? "error" : ""}`}>
              <span className="label">Which air conditioner do you have?</span>
              <select
                name="acType"
                value={form.acType}
                onChange={handleChange}
                onBlur={handleBlur}
                aria-invalid={!!(fieldErrors.acType && touched.acType)}
                aria-describedby={fieldErrors.acType && touched.acType ? "err-actype" : undefined}
              >
                <option value="">Which air conditioner do you have?</option>
                {AC_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
              {fieldErrors.acType && touched.acType && (
                <div id="err-actype" className="field-error">{fieldErrors.acType}</div>
              )}
            </label>
          </div>

          <div className="row">
            <label className={`field full ${fieldErrors.issue && touched.issue ? "error" : ""}`}>
              <span className="label">What issues are you facing?</span>
              <select
                name="issue"
                value={form.issue}
                onChange={handleChange}
                onBlur={handleBlur}
                aria-invalid={!!(fieldErrors.issue && touched.issue)}
                aria-describedby={fieldErrors.issue && touched.issue ? "err-issue" : undefined}
              >
                <option value="">What issues are you facing?</option>
                {ISSUES.map((i) => (
                  <option key={i} value={i}>
                    {i}
                  </option>
                ))}
              </select>
              {fieldErrors.issue && touched.issue && (
                <div id="err-issue" className="field-error">{fieldErrors.issue}</div>
              )}
            </label>
          </div>

          <div className="submit-row">
            <button type="submit" className="btn-submit" aria-disabled={status?.type === "loading"}>
              {status?.type === "loading" ? "Submitting..." : "Submit"}
            </button>
            <div className="note">We will contact you shortly.</div>
          </div>

          {status && (
            <div className={`status ${status.type}`} role={status.type === "error" ? "alert" : "status"}>
              {status.message}
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
